import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, CanComponentDeactivate } from '@klickdata/core/auth';
import { FormHelper } from '@klickdata/core/form';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { QuestionTypes } from '@klickdata/core/question';
import {
    Resource,
    ResourceCategoryService,
    ResourceData,
    ResourceService,
    ResourceTypes,
    AppScope,
} from '@klickdata/core/resource';
import { DialogDisplayImgComponent } from 'apps/klickdata/src/app/shared/dialog/dialog-display-img/dialog-display-img.component';
import * as moment from 'moment';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, map, switchMap, takeUntil } from 'rxjs/operators';
import { ResourceBuilderComponent } from '../resource-builder/resource-builder.component';
import { CreateResourceBaseDirective } from '../resource-create/create-resource-base.directive';

@Component({
    selector: 'app-create-survey',
    templateUrl: './create-survey.component.html',
    styleUrls: ['./create-survey.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateSurveyComponent
    extends CreateResourceBaseDirective
    implements OnInit, AfterViewInit, OnDestroy, OnChanges, CanComponentDeactivate
{
    @Input() resource: Resource;
    @ViewChild(ResourceBuilderComponent) resourceBuilder: ResourceBuilderComponent;
    @Output() saving: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() saved: EventEmitter<Resource> = new EventEmitter<Resource>();
    @Output() published: EventEmitter<{ res: Resource; isParentSubmission?: boolean }> = new EventEmitter<{
        res: Resource;
        isParentSubmission?: boolean;
    }>();
    @Output() onError: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() bottomToolSheet: boolean;
    @Input() publishLabel = $localize`Publish`;
    public questionTypes = [QuestionTypes.CHECKBOX, QuestionTypes.RADIO, QuestionTypes.INPUT, QuestionTypes.TEXTAREA];
    public surveyForm: FormGroup;
    public formats: { [key: string]: string };
    ResourceTypes = ResourceTypes;
    @Input() publishOnly: boolean;
    private publish: boolean;
    public isAdmin$: Observable<boolean>;
    public savingQuestions: boolean;
    AppScope = AppScope;
    private isParentSubmission: boolean;

    constructor(
        protected fb: FormBuilder,
        protected auth: AuthService,
        public router: Router,
        protected route: ActivatedRoute,
        protected categoryService: ResourceCategoryService,
        protected messageService: MessageService,
        protected resourceService: ResourceService,
        protected cdRef: ChangeDetectorRef,
        protected dialog: MatDialog
    ) {
        super(messageService, resourceService, cdRef, dialog);
        this.buildForm();
        this.bottomToolSheet = this.bottomToolSheet || this.route.snapshot.data.bottomToolSheet;
    }

    ngOnInit() {
        this.isAdmin$ = this.auth.getUser().pipe(
            first(),
            map((user) => user.isAdmin())
        );
    }

    ngOnChanges() {
        if (this.resource) {
            this.update();
        }
    }
    public update() {
        this.surveyForm.patchValue({
            id: this.resource.id,
            title: this.resource.title,
            media_id: this.resource.media_id,
            description: this.resource.description,
            priority: this.resource.priority,
            // category_ids: this.resource.category_ids,
            language_id: this.resource.language_id,
        });
        this.cdRef.markForCheck();
        FormHelper.resetForm(this.surveyForm);
    }

    ngAfterViewInit() {
        this.resourceBuilder
            .getResource()
            .pipe(
                filter((resource) => !!resource),
                takeUntil(this.destroy)
            )
            .subscribe((resource) => this.updateForm(resource));
        this.resourceBuilder
            .getService()
            .isLoading()
            .pipe(takeUntil(this.destroy))
            .subscribe((loading) => (this.isLoading = loading));
    }

    updateForm(resource: Resource): void {
        this.surveyForm.patchValue({
            id: this.surveyForm.value.id ? this.surveyForm.value.id : resource.id,
            title: this.surveyForm.value.title ? this.surveyForm.value.title : resource.title,
        });
        FormHelper.resetForm(this.surveyForm);
    }

    public buildForm() {
        this.surveyForm = this.fb.group({
            id: [],
            priority: [],
            title: ['', Validators.required],
            description: [''],
            media_id: [''],
            tag_ids: [[]],
            category_ids: [[], Validators.required],
            language_id: ['', Validators.required],
        });

        this.formats = {
            title: $localize`:@@title:Title`,
            description: $localize`:@@description:Description`,
            category_ids: $localize`:@@categories:Categories`,
        };
    }
    public submitSuvrey() {
        this.isParentSubmission = true;
        this.onSubmit(true);
    }
    /**
     * Save/Publish button action.
     */
    public onSubmit(publish?: boolean) {
        this.publish = publish;
        const itemsReady = this.resourceBuilder.getService().readyForSubmit();
        if (!itemsReady) {
            if (publish) {
                this.messageService.openMessage(MessageErrorComponent, $localize`Questions is required!`);
                this.onError.emit(true);
            } else if (this.surveyForm.dirty) {
                this.submitWithQuestion();
            }
        } else {
            this.savingQuestions = true;
            this.resourceBuilder.getService().submit();
        }
    }

    /**
     * Submit Survey after question saved event emitted from RB!
     */
    public submitWithQuestion() {
        this.savingQuestions = false;
        if (this.surveyForm.invalid) {
            FormHelper.markForm(this.surveyForm);
            this.messageService.openMessage(
                MessageErrorComponent,
                $localize`${FormHelper.formatInvalidForm(this.surveyForm, this.formats)} is missing`
            );
            this.setLoading(false);
        } else {
            this.setLoading(true);
            this.prepareForm()
                .pipe(
                    takeUntil(this.destroy),
                    switchMap((data) =>
                        !data.id ? this.resourceService.store(data) : this.resourceService.update(data)
                    )
                )
                .subscribe((resource) => {
                    this.setLoading(false);
                    this.onSaved(resource);
                    this.cdRef.markForCheck();
                });
        }
    }

    private onSaved(resource: Resource) {
        FormHelper.resetForm(this.surveyForm);
        this.messageService.openMessage(MessageSavedComponent);

        if (this.publish) {
            this.published.emit({ res: resource, isParentSubmission: this.isParentSubmission });
            this.isParentSubmission = false;
        } else {
            this.saved.emit(resource);
        }
    }

    private setLoading(loading: boolean) {
        this.resourceBuilder.getService().setLoading(loading);
        this.saving.emit(loading);
    }

    public prepareForm(): Observable<ResourceData> {
        return combineLatest([this.auth.getUser(), this.resourceBuilder.getResource()]).pipe(
            first(),
            map(([user, resource]) => {
                const data = resource
                    ? resource.getData(this.surveyForm.value)
                    : new Resource(this.surveyForm.value).getData();

                if (!data.id) {
                    data.type_id = ResourceTypes.GeneralSurvey;
                    // data.available_types = ResourceTypes.GeneralSurvey; // Used for displaying alternative test types for the user.
                    data.customer_id = user.customer_id;
                    data.author_id = user.id;
                }
                if (this.publish) {
                    data.published = moment().format('YYYY-MM-DD HH:mm:ss');
                }
                if (!this.publish && data) {
                    data.last_publish = null;
                }
                return data;
            })
        );
    }
    public showImgModal(id: number) {
        this.dialog.open(DialogDisplayImgComponent, {
            maxWidth: '70%',
            maxHeight: '50vh',
            data: id,
        });
    }

    public canDeactivate(): boolean {
        return this.surveyForm.pristine;
    }

    @HostListener('window:beforeunload', ['$event'])
    handleClose($event) {
        $event.returnValue = this.canDeactivate();
    }

    public ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
